import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Paper,
  Autocomplete,
  TextField,
  Button,
  FormControl,
  Typography,
  Box,
  Grid,
  TableContainer,
  Checkbox,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import Cookie from "js-cookie";
import {
  StyledAgGridContainer,
  StyledAlert,
  StyledSnackbar,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { CustomPaper } from "components/Styles/globalStyles";
import { StyledButton, inputStyle } from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";

const RoleAssignment = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const jwt_token = Cookie.get("jwt_token");
  const MInput = inputStyle();
  const [selectedSubMenuIds, setSelectedSubMenuIds] = useState([]);
  const [selectedMenuItems, setSelectedMenuItems] = useState([]);

  const handleCheckboxClick = (submenuId) => {
    setSelectedSubMenuIds((prev) => {
      const updatedIds = prev.includes(submenuId)
        ? prev.filter((id) => id !== submenuId)
        : [...prev, submenuId];

      const updatedItems = rowData.filter((row) => updatedIds.includes(row.submenuId));
      setSelectedMenuItems(updatedItems);

      return updatedIds;
    });
  };

  const handleSelectAll = () => {
    if (selectedSubMenuIds.length === rowData.length) {
      setSelectedSubMenuIds([]);
      setSelectedMenuItems([]);
    } else {
      const allIds = rowData.map((row) => row.submenuId);
      setSelectedSubMenuIds(allIds);
      setSelectedMenuItems(rowData);
    }
  };

  const HeaderCheckbox = () => (
    <Checkbox
      checked={selectedSubMenuIds.length === rowData.length && rowData.length > 0}
      indeterminate={selectedSubMenuIds.length > 0 && selectedSubMenuIds.length < rowData.length}
      onChange={handleSelectAll}
    />
  );

  const columnDefs = [
    {
      field: "addToMenu",
      headerName: "Select All",
      headerComponent: HeaderCheckbox,
      align: "center",
      width: 50,
      cellRenderer: (params) => (
        <Checkbox
          checked={selectedSubMenuIds.includes(params.data.submenuId)}
          onChange={() => handleCheckboxClick(params.data.submenuId)}
        />
      ),
    },
    {
      field: "mainMenu",
      headerName: "Main Menu",
      flex: 1.5,
      headerClass: "custom-header",
      valueFormatter: (params) => params.value || "-",
    },
    {
      field: "subMenuName",
      headerName: "Sub Menu Name",
      flex: 1.5,
      headerClass: "custom-header",
      valueFormatter: (params) => params.value || "-",
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressMovable: true,
  };

  useEffect(() => {
    const fetchRoles = async () => {
      setError(null);
      setLoading(true);
      try {
        const response = await fetch(URLs.hrmsRoleDetails, {
          headers: {
            Authorization: `Bearer ${jwt_token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch roles");
        }
        const data = await response.json();
        const formattedRoles = Array.isArray(data) ? data : data.data || [];
        setRoles(formattedRoles);
      } catch (error) {
        setAlert("Failed to load roles. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleRoleChange = (event, value) => {
    setError(null);
    setRowData([]);
    setSelectedRows(new Set());
    setSelectedRole(value);
  };

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleGoClick = async () => {
    if (!selectedRole?.roleId) {
      setAlert("Please select a role", "error");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${URLs.menuDetailsByRoleId}/${selectedRole.roleId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch menu list: ${response.statusText}`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Invalid response format from server");
      }

      setRowData(data);

      const assignedIds = [];
      const assignedItems = [];

      data.forEach((item) => {
        if (item.permissionStatus === "ASSIGNED") {
          assignedIds.push(item.submenuId);
          assignedItems.push(item);
        }
      });

      // Set the state with populated arrays
      setSelectedSubMenuIds(assignedIds);
      setSelectedMenuItems(assignedItems);
    } catch (error) {
      setAlert(error.message || "Failed to load menu list", "error");
      setRowData([]);
      setSelectedRows(new Set());
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRole?.roleId) {
      setAlert("Please select a role before submitting", "error");
      return;
    }
    const payload = selectedMenuItems.map((item) => ({
      id: 0,
      roleId: selectedRole.roleId,
      submenuId: item.submenuId,
    }));
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const response = await fetch(`${URLs.updateMenuDetails}/${selectedRole.roleId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to update permissions");
      }

      setAlert(
        `Successfully updated permissions for ${selectedRole.roleName || selectedRole.name}`,
        "success"
      );
      setSelectedSubMenuIds([]);
      setSelectedMenuItems([]);
    } catch (error) {
      setAlert("Failed to update permissions. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <DashboardLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          overflow: "auto",
          flexGrow: 1,
          boxShadow: "none",
          border: "none",
          height: "100%",
          backgroundColor: "transparent",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            mt: 1,
            backgroundColor: "transparent",
            boxShadow: "none",
            border: "none",
          }}
        >
          <Box
            sx={{
              overflowX: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, pt: 1, mb: 2, flexDirection: "column" }}>
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <FormControl sx={{ minWidth: 100, width: "20%", maxWidth: "300px" }}>
                  <Autocomplete
                    value={selectedRole}
                    onChange={handleRoleChange}
                    options={roles}
                    getOptionLabel={(option) =>
                      option ? option.roleName || option.name || "" : ""
                    }
                    disableClearable={false}
                    PaperComponent={CustomPaper}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Role"
                        variant="outlined"
                        error={!!error && !selectedRole}
                        sx={{
                          height: 43,
                          ...MInput.b,
                          "& .MuiInputBase-root": {
                            height: "100%",
                            minHeight: 43,
                          },
                        }}
                        InputLabelProps={{
                          sx: {
                            color: MInput.a, // Ensure this applies to the label
                            "&.Mui-focused": {
                              color: "red", // Focused label color
                            },
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>

                <StyledButton onClick={handleGoClick}>GO</StyledButton>
              </Box>
            </Box>

            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                boxShadow: "none",
                border: "none",
              }}
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={15}
              />
            </StyledAgGridContainer>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, mr: 2 }}>
              <StyledButton onClick={handleSubmit} disabled={loading}>
                {loading ? "Saving..." : "Submit"}
              </StyledButton>
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog open={loading} onClose={() => setLoading(false)}>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: 4 }}
        >
          <CircularProgress color="primary" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
    </DashboardLayout>
  );
};

export default RoleAssignment;
